import { formatGr, type Id } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { requestLidlSync } from '../../data/lidl';
import { linePaidGr, useListReceipts } from '../../data/receipts';
import { db } from '../../db/db';
import type { ListRow } from '../../db/types';
import { plural } from '../shop/status';

// Paragon „w drodze”: tyle czasu po zakończeniu zakupów w Lidlu pokazujemy, że czekamy na niego.
const WAIT_MS = 3 * 60 * 60_000;

/** Podsumowanie zakupów: paragon(y) listy z kwotą, oszczędnościami i tym, co czeka na potwierdzenie. */
export function ListReceipts({ list, lidlStore }: { list: ListRow; lidlStore: boolean }) {
  const receipts = useListReceipts(list.id);
  const stats = useLiveQuery(async () => {
    const out = new Map<Id, { toConfirm: number; offCount: number; offGr: number }>();
    for (const r of receipts ?? []) {
      const lines = (await db.receiptLines.where('receiptId').equals(r.id).toArray()).filter((l) => !l.deleted);
      const off = lines.filter((l) => !l.listItemId || l.match === 'rejected');
      out.set(r.id, {
        toConfirm: lines.filter((l) => l.match === 'suggested').length,
        offCount: off.length,
        offGr: off.reduce((s, l) => s + linePaidGr(l), 0),
      });
    }
    return out;
  }, [receipts]);

  const waiting = lidlStore && list.status === 'done' && !!list.finishedMs && Date.now() - list.finishedMs < WAIT_MS && receipts?.length === 0;
  // Koniec zakupów w Lidlu: serwer przez chwilę częściej sprawdza, czy jest już paragon.
  const asked = useRef(false);
  useEffect(() => {
    if (!waiting || asked.current) return;
    asked.current = true;
    void requestLidlSync('finished');
  }, [waiting]);

  if (!receipts) return null;
  if (receipts.length === 0)
    return waiting ? (
      <p className="mx-4 mb-2 rounded-2xl bg-sky-50 p-4 text-sm text-sky-900 dark:bg-sky-900/30 dark:text-sky-100">
        Czekam na paragon z Lidla — zwykle pojawia się kilka minut po zapłacie. Ceny i odhaczenia uzupełnią się same.
      </p>
    ) : null;

  return (
    <div className="mx-4 mb-2 flex flex-col gap-2">
      {receipts.map((r) => {
        const s = stats?.get(r.id);
        const details = [
          r.savingsGr ? `oszczędności ${formatGr(r.savingsGr)}` : null,
          s?.offCount ? `${s.offCount} spoza listy (${formatGr(s.offGr)})` : null,
        ].filter(Boolean);
        return (
          <Link
            key={r.id}
            to={`/receipts/${encodeURIComponent(r.id)}`}
            className="flex items-center gap-3 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm active:bg-stone-50 dark:border-stone-800 dark:bg-stone-900 dark:active:bg-stone-800"
          >
            <span aria-hidden className="text-2xl">
              🧾
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span className="font-semibold">Paragon</span>
                <span className="ml-auto text-xl font-bold tabular-nums">{formatGr(r.totalGr ?? 0)}</span>
              </div>
              {details.length > 0 && <div className="truncate text-sm text-stone-500">{details.join(' · ')}</div>}
              {s?.toConfirm ? (
                <span className="mt-1 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                  {s.toConfirm} {plural(s.toConfirm, 'pozycja', 'pozycje', 'pozycji')} do potwierdzenia
                </span>
              ) : null}
            </div>
          </Link>
        );
      })}
    </div>
  );
}
